// @ts-nocheck

import PropTypes from 'prop-types';
import RefundMessage from './RefundMessage/RefundMessage';
import RefundTypeItem from './RefundTypeItem';
import HeaderItem from './HeaderItem';
import CircularProgress from '@mui/material/CircularProgress';
import { blue } from '@mui/material/colors';
import { format } from 'date-fns';
import { formatCurrency } from '../../../../utils/currencyHelper';
import { IconButton, Radio, Skeleton, Tooltip } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { XGButton } from '@xg-cl/xg-cl';
import { t } from 'i18next';
import { XCircle, CheckCircle } from '@phosphor-icons/react';
import {
  getBookingPaymentInfo,
  requestPaymentRefund
} from '../../../../services/booking/booking';
import { getPassengerName } from '../../../../utils/booking';
import { InfoOutlined } from '@mui/icons-material';
import { REFUND_TYPE } from '../../../../constants/booking';
import './RefundCancelBookingModal.css';

const RefundCancelBookingModal = ({
  booking,
  tripStartDate = null, // We use this prop to get the trip start date temporarily until we get it from the API
  onCancel,
  type
}) => {
  const { bookingNumber } = booking;
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedRefundType, setSelectedRefundType] = useState(null);
  const [refundAmount, setRefundAmount] = useState({ cash: '', partial: '' });
  const [errorMsg, setErrorMsg] = useState({ cash: '', partial: '' });
  const [maxRefundAmount, setMaxRefundAmount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [enableConfirmButton, setEnableConfirmButton] = useState(false);
  const [showRefundedMessage, setShowRefundedMessage] = useState(false);
  const [refundedMessage, setRefundedMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (bookingNumber) {
      setIsLoading(true);
      updateBookingTotals(bookingNumber);
    }
  }, [bookingNumber]);

  const handlePaymentSelect = (event) => {
    setSelectedPayment(event.target.value);
  };

  const handleRefundTypeSelect = (type) => {
    setEnableConfirmButton(true);
    setSelectedRefundType(type);
    setRefundAmount({ cash: '', partial: '' });
    setErrorMsg({ cash: '', partial: '' });
  };

  const updateBookingTotals = async (bookingNumber) => {
    try {
      const res = await getBookingPaymentInfo(bookingNumber);
      setPaymentInfo(res);
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefundCancelBooking = async () => {
    try {
      fullRefundAmount;
      // Bookings need to be ACTIVE to be cancelled

      // Validation in case of partial refund
      if (
        (selectedRefundType == 'partial' && refundAmount.partial <= 0) ||
        refundAmount.partial > maxRefundAmount
      ) {
        return;
      }
      // Validation in case of cash refund
      if (
        (selectedRefundType == 'cash' && refundAmount.cash <= 0) ||
        refundAmount.cash > maxRefundAmount
      ) {
        return;
      }

      // PARTIAL REFUND
      // If the partial refund type of payment is selected,
      // we need to process a partial Refund / cancel the booking
      if (
        selectedRefundType == 'partial' &&
        refundAmount.partial > 0 &&
        refundAmount.partial <= maxRefundAmount
      ) {
        setIsSubmitting(true);

        // call refund booking API
        const { data } = await requestPaymentRefund(selectedPayment, {
          'amount': refundAmount[selectedRefundType], // Required only for partial amounts. Do not convert to cents
          'refundType': selectedRefundType, // full, partial, cash, norefund
          'cancelBooking': type === 'CANCEL_BOOKING' ? true : false, // True to cancel a specific booking
          'bookingId': bookingNumber // (optional), becomes required when cancelling a booking(cancelBooking = true)
        });

        if (data) {
          const successMessage =
            data?.paymentmethod == 'us_bank_account'
              ? t('bookings:achRefundedDescription')
              : t('bookings:refundedDescription', {
                  amount: formatCurrency(refundAmount?.partial || 0)
                });

          await updateBookingTotals(bookingNumber);
          setRefundedMessage(successMessage);
          setShowRefundedMessage(true);
        }
      }
      // CASH REFUND
      // If the cash refund type of payment is selected,
      // we need to process a cash refund / cancel the booking
      if (
        selectedRefundType == 'cash' &&
        refundAmount.cash > 0 &&
        refundAmount.cash <= maxRefundAmount
      ) {
        setIsSubmitting(true);

        // call refund booking API
        const { data } = await requestPaymentRefund(selectedPayment, {
          'amount': refundAmount[selectedRefundType], // Required only for partial amounts. Do not convert to cents
          'refundType': selectedRefundType, // full, partial, cash, norefund
          'cancelBooking': type === 'CANCEL_BOOKING' ? true : false, // True to cancel a specific booking
          'bookingId': bookingNumber // (optional), becomes required when cancelling a booking(cancelBooking = true)
        });

        if (data) {
          setRefundedMessage(
            t('bookings:refundedDescription', {
              amount: formatCurrency(refundAmount?.cash || 0)
            })
          );

          await updateBookingTotals(bookingNumber);
          setShowRefundedMessage(true);
        }
      }
      // FULL REFUND
      // If the full refund type of payment is selected,
      // we need to process a full refund / cancel the booking
      if (selectedRefundType == 'full') {
        setIsSubmitting(true);

        // call refund booking API
        const { data } = await requestPaymentRefund(selectedPayment, {
          'refundType': selectedRefundType, // full, partial, cash, norefund
          'cancelBooking': type === 'CANCEL_BOOKING' ? true : false, // True to cancel a specific booking
          'bookingId': bookingNumber // (optional), becomes required when cancelling a booking(cancelBooking = true)
        });

        if (data) {
          const successMessage =
            data?.paymentmethod == 'us_bank_account'
              ? t('bookings:achRefundedDescription')
              : t('bookings:refundedDescription', {
                  amount: formatCurrency(maxRefundAmount || 0)
                });

          await updateBookingTotals(bookingNumber);
          setRefundedMessage(successMessage);
          setShowRefundedMessage(true);
        }
      }

      // NO REFUND
      // If the no refund type of payment is selected,
      // we need to process a no refund / cancel the booking
      if (selectedRefundType == 'norefund') {
        setIsSubmitting(true);

        // call refund booking API
        const { data } = await requestPaymentRefund(selectedPayment, {
          'refundType': selectedRefundType, // full, partial, cash, norefund
          'cancelBooking': type === 'CANCEL_BOOKING' ? true : false, // True to cancel a specific booking
          'bookingId': bookingNumber // (optional), becomes required when cancelling a booking(cancelBooking = true)
        });

        if (data) {
          setRefundedMessage('');
          setShowRefundedMessage(true);
        }
      }
      // Just Cancel Booking
      // if type === 'CANCEL_BOOKING' and no refund type is selected,
      // we need to process a cancel booking
      if (type === 'CANCEL_BOOKING' && selectedRefundType == null) {
        setIsSubmitting(true);
        // call refund booking API
        const { data } = await requestPaymentRefund(null, {
          'cancelBooking': true, // True to cancel a specific booking
          'bookingId': bookingNumber // (optional), becomes required when cancelling a booking(cancelBooking = true)
        });

        if (data) {
          setRefundedMessage('');
          setShowRefundedMessage(true);
        }
      }
      return;
    } catch (error) {
      setRefundedMessage('');
      setShowRefundedMessage(false);
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRefundedAmount = useCallback((transaction) => {
    return transaction?.refunds?.reduce(
      (acc, refund) => acc + refund?.amount,
      0
    );
  }, []);

  const getRefundableAmount = useCallback(
    (transaction) => {
      return transaction?.amount - getRefundedAmount(transaction);
    },
    [getRefundedAmount]
  );

  useEffect(() => {
    if (!selectedPayment) {
      const canRefund = (transaction) => {
        return getRefundableAmount(transaction) > 0;
      };

      const defaultSelectedPayment = paymentInfo?.data?.transactions?.find(
        (transaction) => canRefund(transaction)
      );
      // If there is a default selected payment, set it as the selected payment
      setSelectedPayment(defaultSelectedPayment?.referencetransactionid);
    }

    // Activate the confirm button if the booking is cancelable
    if (
      type === 'CANCEL_BOOKING' &&
      paymentInfo?.data?.bookingstatus?.statuscode === 'ACTIVE' &&
      !selectedPayment
    ) {
      setEnableConfirmButton(true);
    } else {
      setEnableConfirmButton(false);
    }
  }, [getRefundableAmount, paymentInfo, selectedPayment, type]);

  useEffect(() => {
    if (
      refundAmount[selectedRefundType] <= 0 ||
      refundAmount[selectedRefundType] > maxRefundAmount
    ) {
      setEnableConfirmButton(false);
    } else {
      setEnableConfirmButton(true);
    }
  }, [refundAmount, maxRefundAmount, selectedRefundType]);

  const fullRefundAmount = useMemo(() => {
    const transaction = paymentInfo?.data?.transactions?.find(
      (transaction) => transaction?.referencetransactionid === selectedPayment
    );

    const refundableAmount = getRefundableAmount(transaction);
    // Set the max refund amount to the refundable amount
    setMaxRefundAmount(refundableAmount);
    return refundableAmount;
    // return getRefundableAmount(transaction);
  }, [getRefundableAmount, paymentInfo, selectedPayment]);

  const totalRefundedAmount = useMemo(() => {
    return paymentInfo?.data?.transactions?.reduce((acc, transaction) => {
      return acc + getRefundedAmount(transaction);
    }, 0);
  }, [getRefundedAmount, paymentInfo]);

  const REFUND_TYPES = useMemo(
    () => ({
      REFUND_TYPE_PARTIAL: {
        label: t('bookings:refundTypePartial'),
        value: REFUND_TYPE.partial
      },
      REFUND_TYPE_CASH: {
        label: t('bookings:refundTypeCash'),
        value: REFUND_TYPE.cash
      },
      REFUND_TYPE_FULL: {
        label: t('bookings:refundTypeFull'),
        value: REFUND_TYPE.full
      },
      REFUND_TYPE_NO_REFUND: {
        label: t('bookings:refundTypeNoRefund'),
        value: REFUND_TYPE.norefund
      }
    }),
    []
  );

  if (isLoading) {
    return (
      <>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton
            key={index}
            variant='rectangular'
            width={'100%'}
            height={56}
            sx={{
              borderBottom: '1px solid #EAECF0',
              backgroundColor: '#f5f5f5',
              margin: '24px 0'
            }}
          />
        ))}
      </>
    );
  }

  return (
    <div className='refund-cancel-booking-modal-container'>
      <div className='refund-cancel-booking-modal-header'>
        <h1 className='refund-cancel-booking-modal-title'>
          {type === 'CANCEL_BOOKING'
            ? t('bookings:cancelBooking')
            : t('bookings:refundBooking')}
        </h1>
        <IconButton onClick={onCancel}>
          <XCircle size={32} color='black' />
        </IconButton>
      </div>
      <div className='refund-cancel-booking-modal-content-container'>
        <div className='refund-cancel-booking-header'>
          <HeaderItem
            label={t('bookings:bookingNumber')}
            value={bookingNumber}
          />
          <HeaderItem
            label={t('bookings:bookingDate')}
            value={
              paymentInfo?.data?.bookingdate
                ? format(new Date(paymentInfo?.data?.bookingdate), 'yyyy-MM-dd')
                : ''
            }
          />
          <HeaderItem
            label={t('bookings:departureDate')}
            value={
              // temporary until we get the bookging date from the API
              // paymentInfo?.data?.departuredate
              //   ? format(
              //       new Date(paymentInfo?.data?.departuredate),
              //       'yyyy-MM-dd'
              //     )
              //   : ''
              tripStartDate ? format(new Date(tripStartDate), 'yyyy-MM-dd') : ''
              // this option display the trip start date in readable format
              // tripStartDate ? tripStartDate : ''
            }
          />
          <HeaderItem
            label={t('bookings:totalPrice')}
            value={formatCurrency(paymentInfo?.data?.totalprice)}
          />
          <HeaderItem
            label={t('bookings:amountReceived')}
            value={formatCurrency(paymentInfo?.data?.totalreceived)}
          />
          {totalRefundedAmount > 0 && (
            <HeaderItem
              label={t('bookings:totalRefunded')}
              value={formatCurrency(totalRefundedAmount)}
            />
          )}
        </div>
        <div className='refund-cancel-booking-modal-passengers-container'>
          <h3 className='refund-cancel-booking-modal-section-title'>
            {t('bookings:passengers')}
          </h3>
          <div className='refund-cancel-booking-modal-passengers-list'>
            <ul>
              {paymentInfo?.data?.passangers?.map((passenger) => (
                <li key={passenger.bookingpassengerid}>
                  {getPassengerName(passenger)}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className='refund-cancel-booking-modal-payment-selector-container'>
          <h3 className='refund-cancel-booking-modal-section-title'>
            {t('bookings:selectPayment')}
          </h3>
          <div className='refund-cancel-booking-modal-payment-selector-header'>
            <div></div>
            <h3>{t('bookings:buyerName')}</h3>
            <div className='refund-cancel-booking-modal-method-info'>
              <h3>{t('bookings:method')}</h3>
              <Tooltip title={t('bookings:achRefundsTakesTime')}>
                <InfoOutlined size={16} />
              </Tooltip>
            </div>
            <h3>{t('bookings:payment')}</h3>
            <h3>{t('bookings:paymentAmount')}</h3>
          </div>
          <div className='refund-cancel-booking-modal-payment-selector-items'>
            {paymentInfo?.data?.transactions?.map((transaction) => {
              const paymentmethoddetails =
                transaction?.stripecharge?.paymentmethoddetails;
              const method = paymentmethoddetails?.brand
                ? `${paymentmethoddetails?.funding} - ${paymentmethoddetails?.brand}`
                : `${paymentmethoddetails?.accounttype} - ${paymentmethoddetails?.bank_name}`;

              const refundedAmount = getRefundedAmount(transaction);
              return (
                <div
                  key={transaction.referencetransactionid}
                  className='refund-cancel-booking-modal-payment-selector-item'
                >
                  {refundedAmount === transaction?.amount ? (
                    <div className='refund-cancel-booking-modal-payment-selector-item-icon'>
                      <CheckCircle size={24} color='#079455' />
                    </div>
                  ) : paymentInfo?.data?.transactions?.length > 1 ? (
                    <Radio
                      checked={
                        selectedPayment === transaction.referencetransactionid
                      }
                      onChange={handlePaymentSelect}
                      value={transaction.referencetransactionid}
                    />
                  ) : (
                    <div></div>
                  )}
                  <div className='refund-cancel-booking-modal-payment-selector-item-content'>
                    <h3>{transaction?.stripecharge?.billingdetails?.name}</h3>
                    {transaction?.refunds?.map((refund) => (
                      <p
                        className='refund-cancel-booking-modal-payment-selector-item-refunded'
                        key={refund.referencetransactionid}
                      >
                        {t('bookings:refunded').toUpperCase()}{' '}
                        {formatCurrency(refund?.amount)}{' '}
                        {format(
                          new Date(refund?.transactiondatetime),
                          'dd MMMM yyyy'
                        )}
                      </p>
                    ))}
                  </div>
                  <h3>{method.toUpperCase()}</h3>
                  <h3>
                    {t('bookings:creditCardMask').toUpperCase()}{' '}
                    {transaction?.stripecharge?.paymentmethoddetails?.last4}
                  </h3>
                  <h3>{formatCurrency(transaction?.amount)}</h3>
                </div>
              );
            })}
          </div>
        </div>
        {selectedPayment && !showRefundedMessage && (
          <div className='refund-cancel-booking-modal-refund-type-container'>
            <h3 className='refund-cancel-booking-modal-section-title'>
              {t('bookings:refundType')}
            </h3>
            <div className='refund-cancel-booking-modal-refund-type-items'>
              <RefundTypeItem
                refundType={REFUND_TYPES.REFUND_TYPE_PARTIAL}
                selectedRefundType={selectedRefundType}
                handleRefundTypeSelect={handleRefundTypeSelect}
                customRefundAmount={true}
                refundAmountValue={refundAmount}
                setRefundAmountValue={setRefundAmount}
                maxValue={maxRefundAmount}
                errorMsg={errorMsg}
                setErrorMsg={setErrorMsg}
              />
              <RefundTypeItem
                refundType={REFUND_TYPES.REFUND_TYPE_CASH}
                selectedRefundType={selectedRefundType}
                handleRefundTypeSelect={handleRefundTypeSelect}
                customRefundAmount={true}
                refundAmountValue={refundAmount}
                setRefundAmountValue={setRefundAmount}
                maxValue={maxRefundAmount}
                errorMsg={errorMsg}
                setErrorMsg={setErrorMsg}
              />
              <RefundTypeItem
                refundType={REFUND_TYPES.REFUND_TYPE_FULL}
                selectedRefundType={selectedRefundType}
                handleRefundTypeSelect={handleRefundTypeSelect}
                // refundAmountValue={fullRefundAmount}
                refundAmountValue={maxRefundAmount}
                maxValue={maxRefundAmount}
              />
              {type === 'CANCEL_BOOKING' && (
                <RefundTypeItem
                  refundType={REFUND_TYPES.REFUND_TYPE_NO_REFUND}
                  selectedRefundType={selectedRefundType}
                  handleRefundTypeSelect={handleRefundTypeSelect}
                  refundAmountValue={0}
                  maxValue={maxRefundAmount}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {showRefundedMessage && (
        <RefundMessage
          id='refund-message'
          refundedAmount={100}
          title={t('common:confirmed')}
          description={refundedMessage}
        />
      )}

      <div className='refund-cancel-booking-modal-button-container'>
        {!showRefundedMessage ? (
          <>
            <XGButton
              text={t('common:cancel')}
              type='secondary'
              size='large'
              variant='outlined'
              onClick={onCancel}
              disabled={isSubmitting}
            />
            <div style={{ position: 'relative' }}>
              <XGButton
                disabled={!enableConfirmButton || isSubmitting}
                text={
                  type === 'CANCEL_BOOKING'
                    ? t('bookings:confirmCancelation')
                    : t('bookings:confirmRefund')
                }
                type='primary'
                size='large'
                variant='contained'
                onClick={handleRefundCancelBooking}
              />
              {isSubmitting && (
                <CircularProgress
                  id='refund-cancel-booking-modal-spinner'
                  size={24}
                  sx={{
                    color: blue[500],
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px'
                  }}
                />
              )}
            </div>
          </>
        ) : (
          <></>
          // Rachael requested to hide the button but the design shows it.
          // <XGButton
          //   text={t('common:close')}
          //   type='primary'
          //   size='large'
          //   variant='contained'
          //   onClick={onCancel}
          // />
        )}
      </div>
    </div>
  );
};

RefundCancelBookingModal.propTypes = {
  booking: PropTypes.object.isRequired,
  tripStartDate: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired
};

export default RefundCancelBookingModal;
